import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import moment from 'moment';

@Component({
  standalone: false,
  selector: 'irembogov-custom-report-histogram',
  templateUrl: './custom-report-histogram.component.html',
  styleUrls: ['./custom-report-histogram.component.scss'],
})
export class CustomReportHistogramComponent implements OnChanges {
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;
  @Input() name = '';
  @Input() legends: any[] = [];
  @Input() colors: string[] = [];
  @Input() data: any[] = [];
  @Input() xAxisFormat: any;
  @Input() yAxisFormat: any;
  @Input() xAxisTitle: string | undefined;
  @Input() yAxisTitle: string | undefined;
  @Input() resultFormat: any;
  @Input() resultType = '';
  @Input() showLegend = false;
  @Input() showTitle = false;
  @Input() showXAxisTitle: boolean | undefined;
  @Input() showYAxisTitle: boolean | undefined;
  @Input() renderAsSingleBarchart: boolean | undefined = true;

  isDataAvailable = true;

  public barChartOptions: ChartOptions = {};
  public chartLabels: string[] = [];
  lineChartColors: string[] = [];

  public barChartData: any[] = [];

  ngOnChanges(changes: SimpleChanges) {
    this.isDataAvailable = false;
    const chartData: any[] = [];

    if (changes['colors']?.currentValue) {
      this.lineChartColors = changes['colors'].currentValue.map(
        (c: string) => c
      );
    }

    if (changes['legends']?.currentValue) {
      this.legends = changes['legends']?.currentValue;
    }

    if (changes['data']?.currentValue) {
      this.chartLabels =
        changes['data']?.currentValue?.map(
          (labelObj: { name: string; val: string }) => {
            let label;
            // Getting the label if not null
            // When using SQL, response is {name: string}
            if (labelObj.name) label = labelObj.name;
            // When using SOLR, response is {val: string}
            if (labelObj.val) label = labelObj.val;

            // Apply formatting of xAxis if not null
            if (this.xAxisFormat?.datePattern) {
              label = moment(label).format(this.xAxisFormat?.datePattern);
            }
            return label;
          }
        ) || [];
    }

    if (this.renderAsSingleBarchart) {
      const dataArr: any[] = changes['data']?.currentValue;
      const mappedData = dataArr.map((dataItem, index) => {
        const temp = { ...dataItem };
        delete temp.name; // remove SQL label
        delete temp.val; // remove SOLR label
        return this.getObjValueByIndex(temp, index, this.resultType);
      });

      this.barChartData = [
        {
          data: mappedData,
          stack: 'a',
          backgroundColor: this.lineChartColors[0],
          hoverForeground: '#1061ba',
          label: this.name,
          barPercentage: 0.4,
          borderRadius: 4,
        },
      ];
    } else {
      for (let i = 0; i < this.legends?.length; i++) {
        const legend = this.legends[i];
        const dataArr: any[] = changes['data']?.currentValue;
        // Map data from array by index of label
        const mappedData = dataArr.map(dataItem => {
          const temp = { ...dataItem };
          delete temp.name; // remove SQL label
          delete temp.count; // remove SOLR count
          delete temp.val; // remove SOLR label
          return this.getObjValueByIndex(temp, i, this.resultType);
        });

        chartData.push({
          data: mappedData,
          stack: 'a',
          backgroundColor: this.lineChartColors[i],
          hoverForeground: this.lineChartColors[i],
          label: legend?.name || legend,
        });
      }
      this.barChartData = chartData;
    }

    this.barChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          border: {
            display: false,
          },
          title: {
            display: this.showYAxisTitle,
            text: this.yAxisTitle,
          },
          grid: {
            color: '#d3e3fd',
          },
        },
        x: {
          border: {
            display: false,
          },
          title: {
            display: this.showXAxisTitle,
            text: this.xAxisTitle,
          },
          grid: {
            color: '#ffffff',
          },
        },
      },
    };

    this.isDataAvailable = true;
  }

  getObjValueByIndex(
    obj: Record<any, any>,
    index: number,
    resultType: string
  ): string | number {
    const key = this.legends[index]?.key;
    if (resultType === 'PERCENT') {
      return (obj['subTotal']?.count * 100) / obj['total'] || 0;
    } else {
      if (typeof obj[key] === 'object') {
        return obj[key]?.result || 0;
      } else {
        return obj[key] || 0;
      }
    }
  }
}
